import {
  computed,
  inject,
  Injectable,
  signal,
  type Signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  ActivatedRoute,
  ActivatedRouteSnapshot,
  NavigationEnd,
  NavigationStart,
  Router,
  type NavigationExtras,
} from '@angular/router';
import { filter, forkJoin, map, Observable, of, switchMap } from 'rxjs';

import { takeFirst } from '../../../helpers/observable.helpers';

import {
  type MenuData,
  type ResolvedMenuRoute,
  type MyreRouteData,
} from '../interfaces';
import { AbstractMenuPermissionService } from '../menu/abstract-menu-permission.service';

import {
  areBrothers,
  getBaseUrlFromActivatedRoute,
  getPreviousRouteForCurrentRoute,
} from './routing.helpers';
import {
  type NavigateToBrotherOptions,
  type NavigationHistoryEntry,
} from './routing.interfaces';

@Injectable({ providedIn: 'root' })
export class RoutingService {
  public backButtonStatus: Signal<'loading' | 'visible' | null> = computed(
    () => {
      if (this._pendingBackNavigation()) {
        return 'loading';
      }

      if (this._navigationHistory().length > 1) {
        return 'visible';
      }

      return null;
    },
  );

  public get activatedRouteForBaseUrl(): Signal<ActivatedRouteSnapshot> {
    return this._activatedRouteForBaseUrl;
  }

  public get activatedRouteDataForBaseUrl(): Signal<MyreRouteData> {
    return this._activatedRouteDataForBaseUrl;
  }

  /**
   * The base URL of closest parent route with menu.
   * It's used to build the absolute path of the menu items.
   *
   * It is updated each time a new route with menu data is activated.
   */
  public get baseUrl(): Signal<string> {
    return this._baseUrl;
  }

  private readonly _activatedRoute = inject(ActivatedRoute);
  private readonly _router = inject(Router);
  private readonly _abstractMenuPermissionService = inject(
    AbstractMenuPermissionService,
  );

  private readonly _navigationHistory = signal<NavigationHistoryEntry[]>([]);

  private readonly _pendingBackNavigation = signal<boolean>(false);

  private readonly _baseUrl = computed(() =>
    getBaseUrlFromActivatedRoute(this._activatedRouteForBaseUrl()),
  );

  private readonly _activatedRouteForBaseUrl =
    signal<ActivatedRouteSnapshot>(null);

  private readonly _activatedRouteDataForBaseUrl = computed<MyreRouteData>(
    () => this.activatedRouteForBaseUrl()?.data,
  );

  constructor() {
    this._router.events
      .pipe(
        takeUntilDestroyed(),
        filter(event => event instanceof NavigationStart),
      )
      .subscribe((event: NavigationStart) => {
        if (event.navigationTrigger === 'popstate') {
          this._pendingBackNavigation.set(true);
        }
      });

    this._router.events
      .pipe(
        takeUntilDestroyed(),
        filter(event => event instanceof NavigationEnd),
      )
      .subscribe((_: NavigationEnd) => {
        let route = this._activatedRoute.root.snapshot;

        while (route.firstChild) {
          route = route.firstChild;

          const routeMenuData = route.data?.menu;

          // If menu data is explicitly set to null, then we hide the menu.
          if (routeMenuData === undefined) {
            continue;
          }

          const parentRouteMenuData = route.parent.data?.menu;

          if (routeMenuData !== parentRouteMenuData) {
            if (!this._navigationHistory().length) {
              this._pushInNavigationHistory({
                route,
                url: getBaseUrlFromActivatedRoute(route),
              });
            }

            this._activatedRouteForBaseUrl.set(route);
          }
        }

        const parsedUrl = new URL(this._router.url, window.location.origin);
        const currentUrl = parsedUrl.pathname.substring(1);
        const lastEntry = this._navigationHistory().at(-1);

        // User navigated forward to another route with menu,
        // then the route associated to the current menu must be added to the navigation history.
        if (
          !areBrothers(this._activatedRouteForBaseUrl(), lastEntry?.route) &&
          !this._pendingBackNavigation()
        ) {
          this._pushInNavigationHistory({
            route: this._activatedRouteForBaseUrl(),
            url: currentUrl,
          });
        }

        // User navigated at the same menu level (brothers),
        // update the stored URL so back navigation returns to the correct page.
        if (
          lastEntry &&
          areBrothers(this._activatedRouteForBaseUrl(), lastEntry.route) &&
          !this._pendingBackNavigation()
        ) {
          this._updateLastNavigationHistoryUrl(currentUrl);
        }

        // A brothers step never pushed a new entry going forward, so its back
        // step must revert the URL instead of popping the wrong entry.
        if (this._pendingBackNavigation()) {
          if (
            lastEntry &&
            areBrothers(this._activatedRouteForBaseUrl(), lastEntry.route)
          ) {
            this._updateLastNavigationHistoryUrl(currentUrl);
          } else {
            this._navigationHistory.set(this._navigationHistory().slice(0, -1));
          }
          this._pendingBackNavigation.set(false);
        }
      });
  }

  public navigateToParent(): void {
    this._pendingBackNavigation.set(true);

    const history = this._navigationHistory();

    if (history.length > 1) {
      const previousEntry = history.at(-2);
      void this._router.navigateByUrl(previousEntry.url, {});
    } else {
      // Fallback: use route tree traversal when history is insufficient
      void this._router.navigate(
        [
          getBaseUrlFromActivatedRoute(
            getPreviousRouteForCurrentRoute(
              this._activatedRouteForBaseUrl(),
              history.map(entry => entry.route),
            ),
          ),
        ],
        {},
      );
    }
  }

  public navigateToRoot(options?: {
    commands?: (number | string)[];
    extras?: NavigationExtras;
  }): void {
    void this._router.navigate(options.commands, options.extras ?? {});
    this._navigationHistory.set([]);
  }

  public navigateToCompany(options?: {
    commands: (number | string)[];
    extras?: NavigationExtras;
  }): void {
    const commands: (number | string)[] = ['company'];

    if (options?.commands) {
      commands.push(...options.commands);
    }

    this.navigateToRoot({ commands, extras: options?.extras });
  }

  /**
   * Navigates to given endpoint at the same level as the current route.
   *
   * Example:
   * keepId === true => /company/1/employees -> /company/1/candidates
   * keepId === false => /company/1/employees -> /company/candidates
   *
   * @param options - Options for the navigation.
   * @param options.keepId - Whether to keep the current route id in the URL.
   * @param options.commands - Commands to navigate to.
   * @param options.extras - Navigation extras.
   */
  public navigateToBrother(options: NavigateToBrotherOptions): void {
    const { keepId, commands, extras } = options;

    const baseUrlFromActivatedRoute = getBaseUrlFromActivatedRoute(
      this._activatedRouteForBaseUrl(),
    );

    const baseUrlForNavigation = keepId
      ? baseUrlFromActivatedRoute
      : baseUrlFromActivatedRoute.split('/').slice(0, -1).join('/');

    void this._router.navigate([baseUrlForNavigation, ...commands], extras);
  }

  public computeMenuPermissions$(
    menu$: Observable<MenuData>,
  ): Observable<MenuData> {
    return menu$.pipe(
      takeFirst(),
      switchMap((menu: MenuData) =>
        this._computeRoutesPermissions$(menu.routes).pipe(
          map(routes => ({ ...menu, routes })),
        ),
      ),
    );
  }

  private _computeRoutesPermissions$(
    routes: readonly ResolvedMenuRoute[],
  ): Observable<ResolvedMenuRoute[]> {
    return forkJoin(
      routes.map(route =>
        this._abstractMenuPermissionService.hasAccessToRoute$(route).pipe(
          switchMap(hasAccess => {
            if (!hasAccess) {
              return of(false);
            }

            if (!route.children?.length) {
              return of(true);
            }

            return this._computeRoutesPermissions$(route.children);
          }),
        ),
      ),
    ).pipe(
      map(routeAccesses => routes.filter((_, index) => routeAccesses[index])),
    );
  }

  private _pushInNavigationHistory(entry: NavigationHistoryEntry): void {
    this._navigationHistory.set([...this._navigationHistory(), entry]);
  }

  private _updateLastNavigationHistoryUrl(url: string): void {
    const history = this._navigationHistory();

    if (!history.length) {
      return;
    }

    const updated = [...history];
    updated[updated.length - 1] = { ...updated[updated.length - 1], url };
    this._navigationHistory.set(updated);
  }
}
